import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import * as api from '../../api/educentialApi';
import {
  Users,
  UserCheck,
  BookOpen,
  BookMarked,
  GraduationCap,
  TrendingUp,
  Calendar,
  BarChart3,
  ArrowRight
} from 'lucide-react';

const EducatorDashboard = () => {
  const { user } = useAuth();
  const [dashboardData, setDashboardData] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDashboardData();
    fetchCourses();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const response = await api.getDashboard();
      setDashboardData(response.data);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError(err.message || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const fetchCourses = async () => {
    try {
      const response = await api.getEducatorCourses();
      if (response.success) {
        setCourses(response.data.slice(0, 3)); // Show only 3 recent courses
      }
    } catch (err) {
      console.error('Error fetching courses:', err);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4">
            <p className="text-red-800">Error: {error}</p>
            <button
              onClick={fetchDashboardData}
              className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const { metrics, roleSpecificData, recentActivity, quickActions } = dashboardData || {};
  const educatorData = roleSpecificData || {};

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">
            Welcome, {user?.email?.split('@')[0] || 'Educator'}!
          </h1>
          <p className="text-gray-600 mt-1">Manage students, create content, and track progress</p>
        </div>

        {/* Metrics Cards */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-6 mb-8">
          <MetricCard
            label="Total Students"
            value={metrics?.totalStudents || 0}
            icon={Users}
          />
          <MetricCard
            label="Active Students"
            value={metrics?.activeStudents || 0}
            icon={UserCheck}
          />
          <MetricCard
            label="Content Created"
            value={metrics?.contentCreated || 0}
            icon={BookOpen}
          />
          <MetricCard
            label="Published Content"
            value={metrics?.publishedContent || 0}
            icon={BookMarked}
          />
          <MetricCard
            label="Personal Services"
            value={metrics?.personalServicesEnrolled || 0}
            icon={GraduationCap}
          />
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Quick Actions</h2>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {quickActions?.map((action) => (
              <Link
                key={action.id}
                to={action.actionUrl}
                className="group bg-white p-5 rounded-xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100 hover:border-teal-200"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors">{action.label}</h3>
                    <p className="text-sm text-gray-600 mt-1 line-clamp-2">{action.description}</p>
                  </div>
                  <ArrowRight className="w-5 h-5 text-gray-400 group-hover:text-teal-600 group-hover:translate-x-1 transition-all ml-2 flex-shrink-0" />
                </div>
              </Link>
            ))}
          </div>
        </div>

        {/* My Courses Section */}
        {courses.length > 0 && (
          <div className="mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">My Courses</h2>
              <Link to="/educator/courses" className="flex items-center gap-1 text-teal-600 hover:text-teal-700 text-sm font-medium transition-colors">
                View All Courses <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {courses.map((course) => (
                <EducatorCourseCard key={course.id} course={course} />
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* My Students */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-teal-100 rounded-lg">
                  <Users className="w-5 h-5 text-teal-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">My Students</h2>
              </div>
              <Link to="/educator/students" className="flex items-center gap-1 text-teal-600 hover:text-teal-700 text-sm font-medium transition-colors">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {educatorData.students && educatorData.students.length > 0 ? (
              <div className="space-y-3">
                {educatorData.students.slice(0, 5).map((student) => (
                  <Link
                    key={student.id}
                    to={`/educator/students/${student.id}`}
                    className="group block border border-gray-200 rounded-lg p-4 hover:border-teal-200 hover:bg-teal-50/30 transition-all"
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors">{student.fullName}</p>
                        <p className="text-sm text-gray-600 mt-0.5">{student.email}</p>
                        <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                          <span className="flex items-center gap-1">
                            <BookOpen className="w-3.5 h-3.5" />
                            {student.applicationCount} apps
                          </span>
                          <span className="flex items-center gap-1">
                            <Calendar className="w-3.5 h-3.5" />
                            {new Date(student.lastActivity).toLocaleDateString()}
                          </span>
                        </div>
                      </div>
                      <StatusBadge status={student.status} />
                    </div>
                  </Link>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Users className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500">No students assigned yet</p>
              </div>
            )}
          </div>

          {/* My Content */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex justify-between items-center mb-6">
              <div className="flex items-center gap-2">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <BookOpen className="w-5 h-5 text-blue-600" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900">My Content</h2>
              </div>
              <Link to="/educator/content" className="flex items-center gap-1 text-teal-600 hover:text-teal-700 text-sm font-medium transition-colors">
                View All <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            {educatorData.content && educatorData.content.length > 0 ? (
              <div className="space-y-3">
                {educatorData.content.slice(0, 5).map((content) => (
                  <div key={content.id} className="group border border-gray-200 rounded-lg p-4 hover:border-blue-200 hover:bg-blue-50/30 transition-all">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-semibold text-gray-900 group-hover:text-blue-600 transition-colors">{content.title}</p>
                        <div className="flex items-center gap-2 mt-2">
                          <span className="text-xs bg-gray-100 text-gray-700 px-2.5 py-1 rounded-full font-medium">
                            {content.contentType}
                          </span>
                          <span className="text-xs bg-blue-100 text-blue-700 px-2.5 py-1 rounded-full font-medium">
                            {content.category}
                          </span>
                        </div>
                      </div>
                      {content.published ? (
                        <span className="text-xs bg-green-100 text-green-800 px-2.5 py-1 rounded-full font-medium">
                          Published
                        </span>
                      ) : (
                        <span className="text-xs bg-gray-100 text-gray-800 px-2.5 py-1 rounded-full font-medium">
                          Draft
                        </span>
                      )}
                    </div>
                    <div className="flex items-center gap-3 mt-3 text-xs text-gray-500">
                      <span className="flex items-center gap-1">
                        <BarChart3 className="w-3.5 h-3.5" />
                        {content.viewCount} views
                      </span>
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3.5 h-3.5" />
                        {new Date(content.createdAt).toLocaleDateString()}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <BookOpen className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500 mb-3">No content created yet</p>
                <Link to="/educator/content/new" className="inline-flex items-center gap-2 text-teal-600 hover:text-teal-700 font-medium">
                  Create your first content <ArrowRight className="w-4 h-4" />
                </Link>
              </div>
            )}
          </div>

          {/* Student Analytics */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-6">
              <div className="p-2 bg-purple-100 rounded-lg">
                <BarChart3 className="w-5 h-5 text-purple-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Analytics</h2>
            </div>
            {educatorData.analytics ? (
              <div className="space-y-4">
                <AnalyticItem
                  label="Total Students"
                  value={educatorData.analytics.totalStudents}
                  icon={Users}
                />
                <AnalyticItem
                  label="Active Students"
                  value={educatorData.analytics.activeStudents}
                  icon={UserCheck}
                />
                <AnalyticItem
                  label="Content Created"
                  value={educatorData.analytics.contentCreated}
                  icon={BookOpen}
                />
                <AnalyticItem
                  label="Sessions Completed"
                  value={educatorData.analytics.sessionsCompleted}
                  icon={GraduationCap}
                />
                <AnalyticItem
                  label="Avg. Student Progress"
                  value={`${educatorData.analytics.averageStudentProgress}%`}
                  icon={TrendingUp}
                />
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <BarChart3 className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500">No analytics data available</p>
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
            <div className="flex items-center gap-2 mb-6">
              <div className="p-2 bg-green-100 rounded-lg">
                <Calendar className="w-5 h-5 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold text-gray-900">Recent Activity</h2>
            </div>
            {recentActivity && recentActivity.length > 0 ? (
              <div className="space-y-3 max-h-96 overflow-y-auto custom-scrollbar">
                {recentActivity.slice(0, 10).map((activity) => (
                  <div key={activity.id} className="group border-l-4 border-teal-500 pl-4 py-3 hover:bg-teal-50/30 -ml-6 pl-6 pr-2 rounded-r-lg transition-colors">
                    <p className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors">{activity.title}</p>
                    <p className="text-sm text-gray-600 mt-1">{activity.description}</p>
                    <p className="text-xs text-gray-500 mt-2 flex items-center gap-1">
                      <Calendar className="w-3.5 h-3.5" />
                      {new Date(activity.timestamp).toLocaleString()}
                    </p>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-12">
                <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Calendar className="w-8 h-8 text-gray-400" />
                </div>
                <p className="text-gray-500">No recent activity</p>
              </div>
            )}
          </div>
        </div>

        {/* Personal Services (Educator's own applications) */}
        {educatorData.personalServices && educatorData.personalServices.length > 0 && (
          <div className="mt-8 bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-gray-900">My Personal Services</h2>
              <Link to="/services" className="text-teal-600 hover:text-teal-700 text-sm font-medium">
                View All →
              </Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {educatorData.personalServices.map((app) => (
                <div key={app.id} className="border border-gray-200 rounded-lg p-4">
                  <div className="flex justify-between items-start">
                    <div>
                      <p className="font-medium text-gray-900">
                        {app.service.charAt(0).toUpperCase() + app.service.slice(1)}
                      </p>
                      <p className="text-sm text-gray-600">{app.country}</p>
                    </div>
                    <StatusBadge status={app.status} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// Metric Card Component
const MetricCard = ({ label, value, icon: Icon }) => {
  return (
    <div className="relative bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-6 border border-gray-100 overflow-hidden group">
      <div className="absolute inset-0 bg-teal-600 opacity-0 group-hover:opacity-5 transition-opacity duration-300"></div>
      <div className="relative">
        <div className="flex items-center justify-between mb-3">
          <div className="bg-teal-100 p-3 rounded-lg">
            <Icon className="w-6 h-6 text-teal-600" />
          </div>
        </div>
        <p className="text-sm text-gray-600 font-medium">{label}</p>
        <p className="text-3xl font-bold text-gray-900 mt-1">{value}</p>
      </div>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusColors = {
    ACTIVE: 'bg-green-100 text-green-800',
    INACTIVE: 'bg-gray-100 text-gray-800',
    DRAFT: 'bg-gray-100 text-gray-800',
    SUBMITTED: 'bg-blue-100 text-blue-800',
    UNDER_REVIEW: 'bg-yellow-100 text-yellow-800',
    COMPLETED: 'bg-green-100 text-green-800',
  };

  return (
    <span className={`px-2 py-1 rounded text-xs font-medium ${statusColors[status] || 'bg-gray-100 text-gray-800'}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  );
};

// Analytic Item Component
const AnalyticItem = ({ label, value, icon: Icon }) => {
  return (
    <div className="flex items-center gap-4 border-l-4 border-teal-500 pl-4 py-2">
      <div className="p-2 bg-teal-50 rounded-lg">
        <Icon className="w-5 h-5 text-teal-600" />
      </div>
      <div className="flex-1">
        <p className="text-sm text-gray-600 font-medium">{label}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
      </div>
    </div>
  );
};

// Educator Course Card Component
const EducatorCourseCard = ({ course }) => {
  const totalLessons = course.Modules?.reduce((sum, module) => sum + (module.Lessons?.length || 0), 0) || 0;
  const enrollmentCount = course._count?.Enrollments || 0;

  return (
    <Link
      to={`/educator/courses/edit/${course.id}`}
      className="group bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 hover:border-teal-200 overflow-hidden"
    >
      {/* Course Thumbnail */}
      <div className="relative h-32 bg-gradient-to-br from-teal-400 to-teal-600 overflow-hidden">
        {course.thumbnail ? (
          <img src={course.thumbnail} alt={course.title} className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <BookOpen className="w-10 h-10 text-white opacity-50" />
          </div>
        )}
        {/* Published Badge */}
        {course.published ? (
          <div className="absolute top-2 right-2 bg-green-500 text-white px-2 py-1 rounded text-xs font-semibold">
            Published
          </div>
        ) : (
          <div className="absolute top-2 right-2 bg-gray-500 text-white px-2 py-1 rounded text-xs font-semibold">
            Draft
          </div>
        )}
      </div>

      {/* Course Info */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 group-hover:text-teal-600 transition-colors mb-1 line-clamp-1">
          {course.title}
        </h3>
        <p className="text-xs text-gray-500 mb-3">{course.category} • {course.level}</p>

        {/* Course Stats */}
        <div className="flex items-center justify-between text-xs text-gray-600">
          <div className="flex items-center space-x-1">
            <Users className="w-3.5 h-3.5" />
            <span>{enrollmentCount} student{enrollmentCount !== 1 ? 's' : ''}</span>
          </div>
          <div className="flex items-center space-x-1">
            <BookOpen className="w-3.5 h-3.5" />
            <span>{totalLessons} lesson{totalLessons !== 1 ? 's' : ''}</span>
          </div>
        </div>
      </div>
    </Link>
  );
};

export default EducatorDashboard;
